<script setup lang="ts" generic="T extends object">
import { computed, nextTick, onBeforeUnmount, ref, toRef, watch } from "vue"
import createDebug from "debug"
import { isObject, objectHasKey } from "@/helpers"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faChevronDown, faChevronUp, faSearch } from "@fortawesome/pro-regular-svg-icons"
import type { ZodSchema } from "zod"
import InputText from "@/components/input-text.vue"
import { useUnifiiField } from "@/composables/inputs"
import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/vue"

const debug = createDebug("components:InputSelect");

type ItemValue = any
type Item = { text: string; value: ItemValue }

const props = withDefaults(
  defineProps<{
    items: Readonly<T[]>
    itemText: (keyof T & string) | ((item: T, index: number) => ItemValue)
    itemValue: (keyof T & string) | ((item: T, index: number) => ItemValue)
    disabled?: boolean
    name?: string
    searchable?: boolean
    search?: string

    selected?: ItemValue[] | ItemValue
    multiple?: boolean
    placeholder?: string
    displayLimit?: number
    maxSelected?: number
    rules?: ZodSchema
    inline?: boolean;
    errorVariant?: "inline" | "tooltip"
    required?: boolean
    placement?: "top-start" | "top-end" | "bottom-start" | "bottom-end"
  }>(),
  {
    name: "",
    displayLimit: 3,
    placeholder: "Please select",
    multiple: false,
    disabled: false,
    errorVariant: "inline",
    placement: "bottom-start",
  }
);

const emit = defineEmits<{
  "update:selected": [typeof props.multiple extends true ? ItemValue[] : ItemValue]
  "update:search": [string]
}>();

const $root = ref<HTMLDivElement>()
const $search = ref<InstanceType<typeof InputText>>()

const isExpanded = ref(false)

const $trigger = ref<HTMLElement | null>(null)
const $menu = ref<HTMLElement | null>(null)

const { x, y, strategy, update } = useFloating($trigger, $menu, {
  placement: computed(() => props.placement),
  middleware: [
    offset(6),
    flip(),
    shift({ padding: 8 }),
    size({
      apply({ rects, availableHeight, elements }) {
        Object.assign(elements.floating.style, {
          minWidth: `${rects.reference.width}px`,
          maxHeight: `${Math.min(availableHeight, 500)}px`,
          overflowY: "auto",
        });
      },
    }),
  ],
})

watch(isExpanded, async (expanded) => {
  if(expanded) {
    startAutoUpdate()
    addOutsideListeners()
    await nextTick()
    $search.value?.focus?.()
  }
  else {
    stopAutoUpdate()
    removeOutsideListeners()
    searchValue.value = ""

    $search.value?.reset?.()
  }
})

let cleanupAutoUpdate: (() => void) | null = null

function startAutoUpdate() {
  stopAutoUpdate()
  if ($trigger.value && $menu.value) {
    cleanupAutoUpdate = autoUpdate($trigger.value, $menu.value, update)
  }
}
function stopAutoUpdate() {
  cleanupAutoUpdate?.()
  cleanupAutoUpdate = null
}

function openDropdown() {
  if (props.disabled) return
  isExpanded.value = true

  nextTick(() => {
    startAutoUpdate();
    if (props.searchable) $search.value?.focus?.()
  });

  addOutsideListeners()
}

function toggleDropdown() {
  if (isExpanded.value) isExpanded.value = false
  else openDropdown()
}

function onDocPointerDown(e: PointerEvent) {
  const t = e.target as Node | null
  if (!t) return

  if ($trigger.value?.contains(t)) return
  if ($menu.value?.contains(t)) return

  isExpanded.value = false
}

function onEsc(e: KeyboardEvent) {
  if (e.key === "Escape") isExpanded.value = false
}

function addOutsideListeners() {
  document.addEventListener("pointerdown", onDocPointerDown, true)
  document.addEventListener("keydown", onEsc)
}
function removeOutsideListeners() {
  document.removeEventListener("pointerdown", onDocPointerDown, true)
  document.removeEventListener("keydown", onEsc)
}

onBeforeUnmount(() => {
  stopAutoUpdate()
  removeOutsideListeners()
})

const { validate, setValue, errorMessage, value: currentValue } = useUnifiiField({
  name: toRef(props, "name"),
  rules: toRef(props, "rules"),
  initialValue: toRef(props, "selected"),
})

const internalSearch = ref("")
const isRemoteSearch = computed(() => props.search !== undefined)

const searchValue = computed<string>({
  get: () => props.search ?? internalSearch.value,
  set: (v) => {
    if (props.search === undefined) internalSearch.value = v
    emit("update:search", v)
  }
})

const searchValueTransformed = computed(() => searchValue.value.trim().toLowerCase())

function extractText(item: T, index: number): string | undefined {
  const { itemText } = props

  if (typeof itemText === "function") return itemText(item, index);

  if (isObject(item) && objectHasKey(item, itemText)) {
    return item[itemText]?.toString() || ""
  }

  debug(`Provided [textKey] not found in item.`, { itemText, item, index })

  return undefined;
}

function extractValue(item: T, index: number): ItemValue | undefined {
  const { itemValue } = props

  if (typeof itemValue === "function") return itemValue(item, index);

  if (isObject(item) && objectHasKey(item, itemValue)) {
    return item[itemValue]
  }

  debug(`Provided [valueKey] not found in item.`, { itemValue, item, index })

  return undefined;
}

const allItems = computed(() =>
  (props.items || [])
    .map((item, index): Item | undefined => {
      const value = extractValue(item, index)
      const text = extractText(item, index)

      if(text == undefined)
        debug(`Ignoring item with index [${index}]: item display text couldn't be extracted.`, { item })
      
      if (text === undefined) {
        return undefined;
      }

      return { text: text.toString(), value };
    })
    .filter((x): x is Item => x !== undefined)
)

const itemsForDropdown = computed(() => {
  if (isRemoteSearch.value) return [...allItems.value]
  if (searchValueTransformed.value === "") return [...allItems.value]
  return allItems.value.filter(({ text }) => text.toLowerCase().includes(searchValueTransformed.value))
})

const selectedValues = computed<ItemValue[]>(() => {
  const selected = currentValue.value
  if (selected === undefined) {
     return []
  }

  return Array.isArray(selected) ? selected : [selected]
})

function isSelected(value: ItemValue) {
  return selectedValues.value.indexOf(value) > -1
}

function toggleSelected(value: ItemValue) {
  let values: ItemValue[] = []

  if (!props.multiple) {
    values = [value]
  } else {
    const copy = [...selectedValues.value]
    const idx = copy.indexOf(value)
    const { maxSelected } = props

    if (idx > -1) {
      copy.splice(idx, 1);
    } else if (maxSelected !== undefined && copy.length >= maxSelected) {
      debug("Ignoring item selection. Max number of selected items has been reached.", {
        maxSelected,
        selected: copy,
      })
      return;
    } else {
      debug('Selecting item.', { value })
      copy.push(value)
    }

    values = copy
  }

  const v = props.multiple ? values : values[0]

  setValue(v)

  validate().then(() => emit("update:selected", v))

  if (!props.multiple) isExpanded.value = false
}

const buttonText = computed(() => {
  const { displayLimit, placeholder } = props

  if (allItems.value.length > 0 && selectedValues.value.length === allItems.value.length && props.multiple) {
    return `All selected (${allItems.value.length})`
  }

  const selectedText = allItems.value.filter((item) => isSelected(item.value)).map((item) => item.text)

  if (selectedText.length > displayLimit) return `${selectedText.length} selected`
  if (selectedText.length > 0) return selectedText.join(", ")
  return placeholder
});

function preventCheckboxTogglingIfNotSelected(event: Event, value: ItemValue) {
  if (event.target instanceof HTMLInputElement) {
    event.target.checked = isSelected(value)
  }
}

watch([itemsForDropdown, isExpanded], async ([, expanded]) => {
  if (!expanded) return
  await nextTick()
  update()
});
</script>

<template>
  <div
    :class="[
      $style['input-select'],
      { [$style['input-select-multiple']]: multiple, [$style['input-select-single']]: !multiple, [$style.inline]: inline },
    ]"
    ref="$root"
  >
    <!-- Trigger -->
    <div class="disabled" :class="{ 'is-invalid': errorMessage }">
      <button
        ref="$trigger"
        class="d-block btn btn-outline-secondary text-start"
        :class="[{ [$style.required]: required }, { show: isExpanded }]"
        type="button"
        :disabled="disabled"
        :aria-expanded="isExpanded"
        @click="toggleDropdown"
      >
        <span
          class="d-flex align-items-center justify-content-between"
          v-tooltip="errorVariant === 'tooltip' ? { text: errorMessage, variant: 'error' } : undefined"
        >
          {{ buttonText }}
          <FontAwesomeIcon :icon="isExpanded ? faChevronUp : faChevronDown" class="ms-2" />
        </span>
      </button>
    </div>

    <!-- Menu (teleported out of modal) -->
    <Teleport to="body">
      <ul
        ref="$menu"
        class="dropdown-menu dropdown-menu-end"
        :class="{ show: isExpanded }"
        :style="{
          position: strategy,
          left: x != null ? `${x}px` : '',
          top: y != null ? `${y}px` : '',
          zIndex: 99999
        }"
        @click="multiple && $event.stopPropagation()"
      >
        <li v-if="searchable" class="dropdown-item" :class="$style.search">
          <InputText v-model:value="searchValue" :class="$style['search-input']" ref="$search">
            <template #suffix>
              <FontAwesomeIcon class="small" :icon="faSearch" />
            </template>
          </InputText>
        </li>

        <li class="dropdown-item disabled" v-if="itemsForDropdown.length === 0">
          {{ searchValueTransformed !== "" ? "No items found." : "No items available." }}
        </li>

        <template v-else>
          <li
            :class="{ active: isSelected(item.value) }"
            class="dropdown-item d-flex align-items-center"
            v-for="(item, index) in itemsForDropdown"
            :key="index"
            @click="toggleSelected(item.value)"
            :data-item-index="index"
          >
            <input
              type="checkbox"
              class="mt-0 form-check-input"
              v-if="multiple"
              :value="item.value"
              :checked="isSelected(item.value)"
              @click="preventCheckboxTogglingIfNotSelected($event, item.value)"
            />
            <span :class="{ 'ms-2': multiple }">{{ item.text }}</span>
          </li>
        </template>
      </ul>
    </Teleport>

    <div class="invalid-tooltip" v-if="errorVariant === 'inline' && errorMessage">
      {{ errorMessage }}
    </div>
  </div>
</template>

<style module lang="scss">
@use "@/assets/mixins-form" as mixins;
@use "@/assets/mixins/inputs";
@import "@/assets/variables";

.search {
  padding-left: 1rem;
  padding-right: 1rem;
  margin-top: #{-1 * $dropdown-padding-y};

  &:hover,
  &:focus,
  &:active {
    background-color: transparent !important;
  }

  :global(.input-group-text) {
    padding-left: 0.2rem;
    padding-right: 0.2rem;
  }

  .search-input :global(.form-control),
  .search-input :global(.input-group-text) {
    border-top: none;
    border-left: none;
    border-right: none;
    border-radius: 0;
  }
}

.input-select {
  width: 100%;
  cursor: default;
  position: relative;

  @include mixins.invalid-tooltip;

  :global {
    .dropdown-menu {
      max-height: 500px;
      overflow-y: auto;
      // ✅ When teleported, this helps it look consistent
      display: block;
    }

    .btn.show {
      border-color: $input-focus-border-color;
    }

    .is-invalid > button,
    .is-invalid > .btn.show {
      border-color: var(--#{$prefix}danger);
    }

    .btn {
      --#{$prefix}btn-padding-x: 10px;
      --#{$prefix}btn-padding-y: 6px;

      --#{$prefix}btn-color: #{$body-color};
      --#{$prefix}btn-bg: #fff;
      --#{$prefix}btn-hover-bg: #fff;
      --#{$prefix}btn-hover-color: var(--#{$prefix}btn-color);
      --#{$prefix}btn-hover-border-color: var(--#{$prefix}btn-border-color);
      --#{$prefix}btn-border-color: #d1d1d1;
      --#{$prefix}btn-disabled-border-color: #d1d1d1;
      --#{$prefix}btn-active-bg: var(--#{$prefix}btn-bg);
      --#{$prefix}btn-active-color: #{$body-color};
      --#{$prefix}btn-active-border-color: #d1d1d1;

      position: relative;
      width: 100%;
      transition: none;
    }

    .btn:hover {
      cursor: default;
    }
  }
}

.inline {
  display: inline-block;
  width: auto;

  :global(.dropdown-menu) {
    --bs-dropdown-min-width: auto;
  }
}

.input-select-single {
  :global {
    .dropdown-item:hover,
    .dropdown-item.active {
      background-color: #e0e0e0;
      color: #000;
    }
  }
}

.input-select-multiple {
  :global {
    .dropdown-item.active {
      color: var(--bs-dropdown-color);
      background-color: transparent;
    }

    .dropdown-item.active:hover,
    .dropdown-item:not(:local(.search)):active {
      background-color: #e0e0e0;
      color: #000;
    }

    .form-check-input:focus {
      border-color: var(--#{$prefix}border-color);
    }
  }
}

.required {
  @include inputs.apply-required-indicator-styles(":global(.show)");
}
</style>
